import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const services = [
  {
    href: '/services/web-development',
    gradient: 'from-blue-500 to-cyan-500',
    bgLight: 'bg-blue-50',
    textAccent: 'text-blue-600',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    title: 'Web Development',
    description: 'Modern, responsive web applications with intuitive interfaces, blazing performance, and rock-solid security — built to convert visitors into customers.',
    highlights: ['React / Next.js / Angular', 'Node.js / Python / .NET', 'SEO-Optimized Architecture', 'Enterprise-Grade Security']
  },
  {
    href: '/services/data-analytics',
    gradient: 'from-indigo-500 to-purple-500',
    bgLight: 'bg-indigo-50',
    textAccent: 'text-indigo-600',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Data Analytics',
    description: 'Transform complex, siloed data into real-time dashboards, predictive models, and actionable insights that drive smarter business decisions.',
    highlights: ['ETL Pipelines & Data Warehousing', 'Machine Learning Models', 'Power BI / Tableau Dashboards', 'Real-Time Analytics']
  },
  {
    href: '/services/custom-software-development',
    gradient: 'from-cyan-500 to-blue-500',
    bgLight: 'bg-cyan-50',
    textAccent: 'text-cyan-700',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Custom Software Development',
    description: 'Bespoke software engineered around your unique workflows — giving you full ownership, infinite scalability, and zero vendor lock-in.',
    highlights: ['Full IP Ownership', 'Scalable Microservices', 'Legacy Modernization', 'Agile Delivery Model']
  },
  {
    href: '/services/mobile-app-development',
    gradient: 'from-purple-500 to-pink-500',
    bgLight: 'bg-purple-50',
    textAccent: 'text-purple-600',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Mobile App Development',
    description: 'Native iOS and Android apps — plus cross-platform solutions with React Native and Flutter — designed for engagement, retention, and growth.',
    highlights: ['iOS (Swift) & Android (Kotlin)', 'React Native / Flutter', 'App Store Optimization', 'Offline-First Architecture']
  },
  {
    href: '/services/cloud-solutions',
    gradient: 'from-green-500 to-teal-500',
    bgLight: 'bg-green-50',
    textAccent: 'text-green-700',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    title: 'Cloud Solutions',
    description: 'Secure, scalable cloud infrastructure on AWS, Azure, and GCP — with zero-downtime migrations, cost optimization, and 99.99% uptime SLAs.',
    highlights: ['AWS / Azure / GCP', 'Zero-Downtime Migration', 'Infrastructure as Code', '24/7 Monitoring']
  },
  {
    href: '/services/ui-ux-design',
    gradient: 'from-orange-500 to-red-500',
    bgLight: 'bg-orange-50',
    textAccent: 'text-orange-600',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
      </svg>
    ),
    title: 'UI/UX Design',
    description: 'Research-driven design that creates beautiful, accessible, and conversion-optimized interfaces — backed by usability testing and real user data.',
    highlights: ['User Research & Testing', 'Figma Design Systems', 'WCAG 2.1 Accessibility', 'Conversion Optimization']
  },
  {
    href: '/services/devops-aws-services',
    gradient: 'from-slate-600 to-blue-600',
    bgLight: 'bg-slate-50',
    textAccent: 'text-slate-700',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    title: 'DevOps & AWS Services',
    description: 'CI/CD pipelines, Kubernetes orchestration, and AWS-native automation that compress release cycles from weeks to hours.',
    highlights: ['CI/CD Automation', 'Kubernetes / EKS', 'DevSecOps Integration', 'AWS Cost Optimization']
  }
];

const ServicesPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const fallback = setTimeout(() => setIsVisible(true), 120);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { clearTimeout(fallback); setIsVisible(true); } },
      { threshold: 0.05 }
    );
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      clearTimeout(fallback);
      if (contentRef.current) observer.unobserve(contentRef.current);
    };
  }, []);

  return (
    <>
      <title>IT Services Dubai | Software Development, Cloud &amp; AI Solutions</title>
      <meta name="description" content="Discover Ascali's IT services including software development, web development, mobile apps, cloud solutions, data analytics, AI development, and DevOps services in Dubai, UAE." />
      <Header />

      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10 text-center text-white">
          <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest mb-4 bg-white/10 px-4 py-1.5 rounded-full">
            What We Do
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-cyan-300">Services</span>
          </h1>
          <div className="w-24 h-1 bg-white/40 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            From custom software and mobile apps to cloud infrastructure and data intelligence — Ascali delivers end-to-end technology solutions that power business transformation.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '7', label: 'Core Services' },
              { value: '200+', label: 'Projects Delivered' },
              { value: '50+', label: 'Technologies Mastered' },
              { value: '98%', label: 'Client Satisfaction' }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold gradient-text font-display mb-1">{stat.value}</div>
                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={contentRef} className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Explore Our <span className="gradient-text">Service Offerings</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Each service is backed by deep expertise, proven methodologies, and a commitment to measurable outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl shadow-sm hover:shadow-xl border border-slate-100 overflow-hidden card-hover group ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <div className={`h-2 bg-gradient-to-r ${service.gradient}`}></div>
                <div className="p-7">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-600 text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient} flex-shrink-0`}></span>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={service.href}
                    className={`inline-flex items-center gap-2 ${service.textAccent} font-semibold text-sm hover:gap-3 transition-all duration-200`}
                  >
                    Learn More
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ascali */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 md:p-16 text-white ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Why Businesses Choose Ascali</h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">One technology partner for every layer of your digital transformation.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              {[
                {
                  icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
                  title: 'Proven Track Record',
                  desc: '200+ successfully delivered projects across industries including finance, healthcare, retail, and logistics.'
                },
                {
                  icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
                  title: 'End-to-End Ownership',
                  desc: 'Strategy, design, development, deployment, and support — we cover every phase so you never need to manage multiple vendors.'
                },
                {
                  icon: <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                  title: 'Agile & Transparent',
                  desc: 'Two-week sprints, regular demos, and full visibility into progress. You are never left wondering what is happening with your project.'
                }
              ].map((item, i) => (
                <div key={i} className="text-left">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-xl text-cyan-300 mb-3">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="container-custom text-center">
          <div className={`max-w-2xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Not sure which service you need?
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Tell us about your business challenge and our team will recommend the right solution.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Talk to Our Experts
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ServicesPage;
